import React, { useCallback, useEffect, useRef, useState } from "react";
import {Modal, Button, ModalProps} from "react-bootstrap";
import { useField, useFormikContext } from "formik";
import Webcam from "react-webcam";
import UIButton from "./UIButton";
import {Images, RefreshCcw, RotateCcw} from "lucide-react";

// Helper: dataURL → Blob
function dataURLToBlob(dataUrl: string): Blob {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] ?? "image/jpeg";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
}

export interface CameraPreviewProps {
    onCapture?: (blob: Blob) => void;
    onPreview?: (url: string | null) => void;
    name: string;
    toFileName?: string;
    aspect?: number;
    onClose?: () => void;
    autoCloseOnCapture?: boolean;
    screenshotQuality?: number;
}

const CameraPreview: React.FC<CameraPreviewProps> = ({
                                                         onCapture = (blob) => console.log("Captured", blob),
                                                         onPreview,
                                                         name,
                                                         toFileName,
                                                         aspect = 3 / 4,
                                                         onClose,
                                                         autoCloseOnCapture = true,
                                                         screenshotQuality = 0.92,
                                                     }) => {
    const webcamRef = useRef<Webcam | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [torchOn, setTorchOn] = useState(false);

    const [shotUrl, setShotUrl] = useState<string | null>(null);
    const [facing, setFacing] = useState<"environment" | "user">("environment");
    const [isReady, setIsReady] = useState(false);

    const { setFieldValue, setFieldTouched } = useFormikContext<any>();
    const [field, meta] = useField<Blob | File | null>(name);

    // Stop active tracks
    const stopTracks = useCallback(() => {
        try {
            // @ts-expect-error react-webcam keeps stream ref
            const stream: MediaStream | undefined = webcamRef.current?.stream;
            stream?.getTracks().forEach((t) => t.stop());
        } catch {}
    }, []);

    const handleClose = useCallback(() => {
        stopTracks();
        onClose?.();
    }, [onClose, stopTracks]);

    const capture = useCallback(async () => {
        const video = webcamRef.current;
        if (!video) return;
        const dataUrl = video.getScreenshot?.();
        if (!dataUrl) return;

        const blob = dataURLToBlob(dataUrl);
        const value = toFileName ? new File([blob], toFileName, { type: blob.type }) : blob;

        await setFieldValue(name, value, true);
        await setFieldTouched(name, true, false);

        setShotUrl(dataUrl);           // in-component preview
        onPreview?.(dataUrl);          // parent preview
    }, [name, setFieldValue, setFieldTouched, onPreview, toFileName]);

    const usePhoto = useCallback(() => {
        if (!shotUrl) return;

        if (shotUrl.startsWith("data:")) {
            const blob = dataURLToBlob(shotUrl);
            onCapture?.(blob);
            if (autoCloseOnCapture) handleClose();
        } else if (shotUrl.startsWith("blob:")) {
            fetch(shotUrl)
                .then((res) => res.blob())
                .then((blob) => {
                    onCapture?.(blob);
                    if (autoCloseOnCapture) handleClose();
                });
        }
    }, [shotUrl, onCapture, autoCloseOnCapture, handleClose]);

    const retake = () => {
        if (shotUrl && shotUrl.startsWith("blob:")) URL.revokeObjectURL(shotUrl);
        setShotUrl(null);
        onPreview?.(null); // clear parent preview too
    };

    const onPickFromGallery = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Put the file into Formik so it’s ready to submit
        void (async () => {
            await setFieldValue(name, toFileName ? new File([file], toFileName, { type: file.type }) : file, true);
            await setFieldTouched(name, true, false);
        })();

        // Local + parent preview
        if (shotUrl && shotUrl.startsWith("blob:")) URL.revokeObjectURL(shotUrl);
        const url = URL.createObjectURL(file);
        setShotUrl(url);
        onPreview?.(url);

        // allow selecting the same file again later
        e.currentTarget.value = "";
    };

    useEffect(() => {
        return () => {
            if (shotUrl && shotUrl.startsWith("blob:")) URL.revokeObjectURL(shotUrl);
        };
    }, [shotUrl]);

    const wrapperStyle: React.CSSProperties = {
        position: "relative",
        width: "100%",
        height: `calc(100vw / ${1 / aspect})`,
        maxHeight: "100vh",
        background: "black",
        overflow: "hidden",
    };

    return (
        <div className="d-flex flex-column w-100 h-100 bg-dark text-white">
            {/* Top bar */}
            <div className="d-flex justify-content-between align-items-center p-2 d-none">
                <Button variant="light" size="sm" onClick={handleClose}>
                    ✕
                </Button>
                <Button
                    variant="light"
                    size="sm"
                    onClick={() => setFacing(facing === "user" ? "environment" : "user")}
                >
                    Flip
                </Button>
            </div>

            {/* Viewfinder */}
            <div className="flex-fill d-flex align-items-center justify-content-center">
                <div style={wrapperStyle}>
                    {!shotUrl && (
                        <Webcam
                            ref={webcamRef}
                            audio={false}
                            mirrored={facing === "user"}
                            screenshotFormat="image/jpeg"
                            screenshotQuality={screenshotQuality}
                            onUserMedia={() => setIsReady(true)}
                            onUserMediaError={() => setIsReady(false)}
                            videoConstraints={{
                                facingMode: { ideal: facing },
                                width: { ideal: 1920 },
                                height: { ideal: 1080 },
                                aspectRatio: aspect,
                            }}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    )}
                    {shotUrl && (
                        <img
                            src={shotUrl}
                            alt="Captured"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    )}
                </div>
            </div>

            {/* Controls */}
            <div className="d-flex justify-content-around align-items-center p-3">
                <UIButton
                    id="gallery-btn"
                    variant="outline-light"
                    clickFunction={() => fileInputRef.current?.click()}
                    Icon={Images}
                ></UIButton>
                {!shotUrl ? (
                    <Button
                        variant="light"
                        className="rounded-circle"
                        style={{ width: 70, height: 70, border: 'solid 10px gray'}}
                        onClick={capture}
                        disabled={!isReady}
                    />
                ) : (
                    <>
                        <Button variant="secondary" onClick={retake}>
                            Retake
                        </Button>
                        <Button variant="primary" onClick={usePhoto}>
                            Use
                        </Button>
                    </>
                )}
                <UIButton
                    id="rotate"
                    Icon={RefreshCcw}
                    variant="outline-light"
                    clickFunction={() => setFacing(facing === "user" ? "environment" : "user")}
                ></UIButton>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={onPickFromGallery}
            />
        </div>
    );
};

export default CameraPreview;

export interface CameraRBModalProps extends Pick<ModalProps, 'show' | 'onHide' | 'backdrop' | 'keyboard' | 'centered'> {
    onCapture?: (blob: Blob) => void;
    onPreview?: (url: string | null) => void;
    toFilename?: string;
    name: string;
    aspect?: number;
    /** Make it fullscreen on mobile; pass true | 'sm' | 'md' | 'lg' | 'xl' per RB docs */
    fullscreen?: true | 'sm-down' | 'md-down' | 'lg-down' | 'xl-down' | 'xxl-down';
    title?: string;
}

export const CameraRBModal: React.FC<CameraRBModalProps> = ({
                                                                show,
                                                                onHide,
                                                                onPreview,
                                                                onCapture,
                                                                aspect,
                                                                backdrop = 'static',
                                                                keyboard = true,
                                                                centered = true,
                                                                fullscreen = true,
                                                                title = 'Camera',
                                                                name,
                                                                toFilename,
                                                            }) => {
    return (
        <Modal
            show={!!show}
            onHide={onHide}
            backdrop={backdrop}
            keyboard={keyboard}
            centered={centered}
            fullscreen={fullscreen}
            contentClassName="bg-dark text-white border-0"
        >
            <Modal.Header closeButton closeVariant="white" className="border-0 w-">
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0" style={{ minHeight: '60vh' }}>
                {show && (
                    <CameraPreview onCapture={onCapture} aspect={aspect} onClose={onHide} onPreview={onPreview} name={name} toFileName={toFilename} />
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 d-none" />
        </Modal>
    );
};
